// The 표 screen's store, wired to the app: the API, a calc Web Worker, the
// socket's changes (engine.ts), the stage-1 rows for the built-in names,
// toasts. One store at a time, for the workbook on screen; it stays while
// the person goes to other screens and back, and goes when another
// workbook is opened (its worker with it).
import { useSyncExternalStore } from 'react'

import { showToast } from '../../ui/toast'
import { type Id, tapi } from '../api'
import { CalcClient } from '../calc/calcClient'
import { engine, onTensorChanges } from '../engine'
import { type Book, type RowOp, shownRows } from '../model'
import { getState, subscribe } from '../store'
import { type GridDeps, GridStore, type Ledger } from './store'

let current: GridStore | null = null
let watching = false

/** Signed out, or the workbook is gone: the store goes too. */
function watch(): void {
  if (watching) return
  watching = true
  subscribe(() => {
    const s = getState()
    if (current && (s.phase === 'signed-out' || (s.phase === 'ready' && !s.workbooks[current.workbookId]))) dropGridStore()
  })
}

function ledgerOf(workbookId: Id): () => Ledger | null {
  let cache: { book: Book; ops: RowOp[]; ledger: Ledger } | null = null
  return () => {
    const s = getState()
    const book = s.books[workbookId]
    if (!book || book.loading) return cache?.ledger ?? null
    if (cache && cache.book === book && cache.ops === s.ops) return cache.ledger
    const prev = cache?.ledger
    const rows =
      prev && cache && cache.book.rows === book.rows && cache.ops === s.ops ? prev.rows : shownRows(book, s.ops, workbookId, s.me?.id ?? 0)
    const ledger: Ledger = {
      rows,
      items: book.items,
      budgets: book.budgets,
      dims: book.dims,
      tensors: book.tensors,
      entries: book.entries,
    }
    cache = { book, ops: s.ops, ledger }
    return ledger
  }
}

function deps(workbookId: Id): GridDeps {
  return {
    api: tapi,
    calc: CalcClient.worker(),
    toast: showToast,
    signedOut: () => engine.signedOut('다시 로그인해 주세요.'),
    ledger: ledgerOf(workbookId),
    onLedger: (l) => subscribe(l),
    onChanges: onTensorChanges,
    onOnline: (l) => {
      let was = getState().connection
      return subscribe(() => {
        const now = getState().connection
        if (now === 'online' && was !== 'online') l()
        was = now
      })
    },
  }
}

/** The store of a workbook (made when first asked for). */
export function gridStore(workbookId: Id): GridStore {
  if (current && current.workbookId === workbookId) return current
  watch()
  current?.dispose()
  current = new GridStore(workbookId, deps(workbookId))
  void current.start()
  return current
}

/** Drops the store (signed out, or the workbook is gone). */
export function dropGridStore(): void {
  current?.dispose()
  current = null
}

/** The store and its version (the screen draws again when it changes). */
export function useGrid(workbookId: Id): GridStore {
  const store = gridStore(workbookId)
  useSyncExternalStore(store.subscribe, store.getVersion)
  return store
}

/** A calc worker of its own (an import that renames sheets). */
export function scratchCalc(): CalcClient {
  return CalcClient.worker()
}
